<script setup>
import { computed } from "vue";

const props = defineProps({
  source: {
    type: String,
    default: "",
  },
  size: {
    type: Number,
    default: 28,
  },
  // When true, render the real brand logo on a brand-coloured tile (matches the
  // onboarding flow). Default false keeps the compact coloured-letter dot used
  // across dense tables and lists.
  logo: {
    type: Boolean,
    default: false,
  },
});

const MARKS = {
  GOOGLE: { color: "#4285F4", glyph: "G" },
  FACEBOOK: { color: "#1877F2", glyph: "f" },
  TRIPADVISOR: { color: "#1E9D6B", glyph: "T" },
  HELLOPETER: { color: "#1C3FAA", glyph: "H" },
  TRUSTPILOT: { color: "#00B67A", glyph: "★" },
  FRESHA: { color: "#6950F3", glyph: "F" },
  PLAYSTORE: { color: "#01875F", glyph: "▶" },
  INSTAGRAM: { color: "#E4405F", glyph: "◎" },
};

// Brand tile backgrounds for the real-logo variant.
const LOGO_TILES = {
  GOOGLE: { bg: "#fff", border: true },
  HELLOPETER: { bg: "#16357A" },
  TRIPADVISOR: { bg: "#00AA6C" },
  FACEBOOK: { bg: "#1877F2" },
  FRESHA: { bg: "#1A1A1A" },
  TRUSTPILOT: { bg: "#00B67A" },
  PLAYSTORE: { bg: "#01875F" },
  INSTAGRAM: {
    bg: "linear-gradient(45deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)",
  },
};

const normalized = computed(() => props.source?.toUpperCase?.() || "");
const label = computed(() =>
  normalized.value
    .replace("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase()),
);
const mark = computed(
  () =>
    MARKS[normalized.value] || {
      color: "var(--neu)",
      glyph: label.value.charAt(0) || "?",
    },
);
const tile = computed(
  () => LOGO_TILES[normalized.value] || { bg: "var(--surface-2)" },
);
</script>

<template>
  <span
    v-if="logo"
    class="bt-srclogo"
    :style="{
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: `${Math.round(size * 0.28)}px`,
      background: tile.bg,
      border: tile.border ? '1px solid var(--border)' : 'none',
    }"
    :title="label"
  >
    <svg v-if="normalized === 'GOOGLE'" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-2 3.2-4.9 3.2-7.9z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.7c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.1-1.9-6-4.4H2.3v2.8A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M6 14.4a6.6 6.6 0 0 1 0-4.2V7.4H2.3a11 11 0 0 0 0 9.8z"
      />
      <path
        fill="#EA4335"
        d="M12 5.4c1.6 0 3 .5 4.1 1.6l3.1-3.1A11 11 0 0 0 2.3 7.4L6 10.2c.9-2.5 3.2-4.4 6-4.4z"
      />
    </svg>
    <svg
      v-else-if="normalized === 'HELLOPETER'"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 3V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
      <path d="M8.5 10.2l2 2 4.5-4.3" />
    </svg>
    <svg v-else-if="normalized === 'TRIPADVISOR'" viewBox="0 0 24 24">
      <circle cx="8" cy="12" r="3.4" fill="#fff" />
      <circle cx="16" cy="12" r="3.4" fill="#fff" />
      <circle cx="8" cy="12" r="1.4" fill="#00AA6C" />
      <circle cx="16" cy="12" r="1.4" fill="#00AA6C" />
    </svg>
    <svg v-else-if="normalized === 'FACEBOOK'" viewBox="0 0 24 24" fill="#fff">
      <path
        d="M14 8.5V6.8c0-.8.2-1.3 1.4-1.3H17V2.3C16.4 2.2 15.4 2 14.3 2 11.9 2 10.3 3.5 10.3 6.2v2.3H8v3.3h2.3V22H14v-10.2h2.6l.4-3.3z"
      />
    </svg>
    <svg v-else-if="normalized === 'FRESHA'" viewBox="0 0 24 24" fill="#fff">
      <path
        d="M12 4c.6 4.6 2.8 6.8 7.4 7.4-4.6.6-6.8 2.8-7.4 7.4-.6-4.6-2.8-6.8-7.4-7.4C9.2 10.8 11.4 8.6 12 4z"
      />
    </svg>
    <svg
      v-else-if="normalized === 'TRUSTPILOT'"
      viewBox="0 0 24 24"
      fill="#fff"
    >
      <path
        d="M12 2.5l2.9 6.2 6.8.6-5.1 4.5 1.5 6.7L12 17.6 5.9 21l1.5-6.7L2.3 9.3l6.8-.6z"
      />
    </svg>
    <svg v-else-if="normalized === 'PLAYSTORE'" viewBox="0 0 24 24" fill="#fff">
      <path d="M6 3.5v17l13-8.5z" />
    </svg>
    <svg
      v-else-if="normalized === 'INSTAGRAM'"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      stroke-width="2"
    >
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17" cy="7" r="1.2" fill="#fff" stroke="none" />
    </svg>
    <span v-else class="bt-srclogo-fallback">{{ mark.glyph }}</span>
  </span>

  <span
    v-else
    class="bt-srcdot"
    :style="{
      background: mark.color,
      width: `${size}px`,
      height: `${size}px`,
      fontSize: `${size * 0.5}px`,
    }"
    :title="label"
  >
    {{ mark.glyph }}
  </span>
</template>

<style scoped>
.bt-srclogo {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  overflow: hidden;
}
.bt-srclogo svg {
  width: 58%;
  height: 58%;
  display: block;
}
.bt-srclogo-fallback {
  color: #fff;
  font-weight: 800;
  font-size: 55%;
}
</style>
